import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chatApi, ChatRoom, Message } from '../api/chatApi';
import { apartmentsApi } from '../api/apartmentsApi';
import { BuildingOption } from '../api/feeCategoriesApi';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../utils/buildingSelection';
import { useLang } from '../contexts/LangContext';
import { AsyncSearchPicker } from '../components/common/AsyncSearchPicker';
import { 
  UserIcon,
  CalendarIcon,
  ClockIcon,
  PaperAirplaneIcon,
  ArrowLeftIcon,
  ChatBubbleLeftIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PhotoIcon,
  PaperClipIcon,
  XMarkIcon,
  LockClosedIcon,
  LockOpenIcon,
  ExclamationCircleIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { io, Socket } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { fileAccessApi } from '../api/fileAccessApi';
import { ImageModal } from '../components/ImageModal';
import { formatApartmentChatTitle, formatApartmentResidentLabel } from '../utils/apartmentDisplay';
import { getBackendOrigin } from '../utils/apiBaseUrl';

// Component for rendering individual messages with secure file URLs
const SecureMessage: React.FC<{ 
  message: Message; 
  user: any; 
  onImageClick: (imageUrl: string, fileName: string) => void;
  room?: ChatRoom | null;
}> = ({ message, user, onImageClick, room }) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [fileUrl, setFileUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('vi-VN', { month: 'short', day: 'numeric' });
    }
  };

  // Helper function to get the correct file URL
  const getFileUrl = async (fileUrl: string): Promise<string> => {
    // If the URL already starts with http/https, it's a complete URL (S3)
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      return fileUrl;
    }
    
    // If it's an S3 reference URL, get a pre-signed URL
    if (fileUrl.startsWith('s3://')) {
      try {
        return await fileAccessApi.getSecureFileUrl(fileUrl);
      } catch (error) {
        console.error('Error getting secure file URL:', error);
        return fileUrl; // Fallback to original URL
      }
    }
    
    // Otherwise, it's a local file path, prepend the base URL
    return `${getBackendOrigin()}${fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`}`;
  };

  // Load secure URLs when component mounts
  useEffect(() => {
    const loadSecureUrls = async () => {
      if (message.messageType === 'IMAGE' && message.fileUrl) {
        setIsLoading(true);
        try {
          const secureUrl = await getFileUrl(message.fileUrl);
          setImageUrl(secureUrl);
        } catch (error) {
          console.error('Error loading image URL:', error);
          setImageUrl(message.fileUrl);
        } finally {
          setIsLoading(false);
        }
      }
      
      if (message.messageType === 'FILE' && message.fileUrl) {
        try {
          const secureUrl = await getFileUrl(message.fileUrl);
          setFileUrl(secureUrl);
        } catch (error) {
          console.error('Error loading file URL:', error);
          setFileUrl(message.fileUrl);
        }
      }
    };

    loadSecureUrls();
  }, [message.fileUrl, message.messageType]);

  const apartmentParticipant = room?.participants.find((p) => p.apartment);
  const senderLabel =
    message.senderType === 'RESIDENT' && apartmentParticipant?.apartment
      ? formatApartmentResidentLabel(apartmentParticipant.apartment)
      : message.sender?.name || 'Unknown';
  
  return (
    <div className={`flex ${message.senderId === user?.id ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-xs lg:max-w-md xl:max-w-lg ${message.senderId === user?.id ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'} rounded-lg px-4 py-2 shadow-sm`}>
        <div className="flex items-center space-x-2 mb-1">
          <span className="text-xs opacity-75">
            {senderLabel} • {formatMessageTime(message.createdAt)}
          </span>
        </div>
        
        {message.content && (
          <div className="mb-2">
            {message.content}
          </div>
        )}
        
        {message.messageType === 'IMAGE' && message.fileUrl && (
          <div className="mt-2">
            {isLoading ? (
              <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-current"></div>
              </div>
            ) : imageUrl ? (
              <div className="relative">
                <img 
                  src={imageUrl} 
                  alt={message.fileName || 'Image'} 
                  className="max-w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => onImageClick(imageUrl, message.fileName || 'Image')}
                  style={{ maxHeight: '300px' }}
                />
                <div className="text-xs mt-1 opacity-75">
                  {message.fileName}
                </div>
              </div>
            ) : (
              <div className="text-red-500 text-sm">Failed to load image</div>
            )}
          </div>
        )}
        
        {message.messageType === 'FILE' && message.fileUrl && (
          <div className="mt-2">
            {fileUrl ? (
              <a 
                href={fileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 underline"
              >
                <PaperClipIcon className="h-4 w-4" />
                <span>{message.fileName || 'Download file'}</span>
              </a>
            ) : (
              <div className="text-red-500 text-sm">Failed to load file</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ChatPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshCounts } = useNotifications();
  const { t } = useLang();
  
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showAddChatModal, setShowAddChatModal] = useState(false);
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [selectedApartmentId, setSelectedApartmentId] = useState('');
  const [selectedApartmentLabel, setSelectedApartmentLabel] = useState('');
  
  // Image modal state
  const [imageModal, setImageModal] = useState<{
    isOpen: boolean;
    imageUrl: string;
    fileName: string;
  }>({
    isOpen: false,
    imageUrl: '',
    fileName: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const selectedRoomIdRef = useRef<string | null>(null);
  const userIdRef = useRef<string | undefined>(undefined);
  const routeRoomIdRef = useRef<string | undefined>(roomId);
  const socketRef = useRef<Socket | null>(null);
  const roomsRef = useRef<ChatRoom[]>([]);
  const loadRoomsRef = useRef<(silent?: boolean) => Promise<void>>(async () => {});
  const syncOpenChatMessagesRef = useRef<() => Promise<void>>(async () => {});
  const refreshCountsRef = useRef(refreshCounts);

  const getActiveChatRoomId = useCallback((): string | null => {
    return selectedRoomIdRef.current ?? routeRoomIdRef.current ?? null;
  }, []);

  const syncOpenChatMessages = useCallback(async () => {
    const activeId = getActiveChatRoomId();
    if (!activeId) {
      return;
    }
    try {
      const messageData = await chatApi.getMessages(activeId);
      const ordered = messageData.reverse();
      setMessages((prev) => {
        const prevLatest = prev[prev.length - 1]?.id;
        const apiLatest = ordered[ordered.length - 1]?.id;
        if (prevLatest && apiLatest && prevLatest === apiLatest && prev.length === ordered.length) {
          return prev;
        }
        return ordered;
      });
    } catch (error) {
      console.error('Error syncing open chat messages:', error);
    }
  }, [getActiveChatRoomId]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  const joinAllChatSocketRooms = useCallback(() => {
    const sock = socketRef.current;
    if (!sock?.connected) {
      return;
    }
    for (const room of roomsRef.current) {
      sock.emit('joinRoom', { roomId: room.id });
    }
  }, []);

  const loadRooms = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoadingRooms(true);
      }
      const roomData = await chatApi.getRooms();
      roomsRef.current = roomData;
      setRooms(roomData);
      joinAllChatSocketRooms();
      await syncOpenChatMessagesRef.current();
    } catch (error) {
      console.error('Error loading rooms:', error);
    } finally {
      if (!silent) {
        setLoadingRooms(false);
      }
    }
  }, [joinAllChatSocketRooms]);

  useEffect(() => {
    loadRoomsRef.current = loadRooms;
  }, [loadRooms]);

  useEffect(() => {
    syncOpenChatMessagesRef.current = syncOpenChatMessages;
  }, [syncOpenChatMessages]);

  useEffect(() => {
    refreshCountsRef.current = refreshCounts;
  }, [refreshCounts]);

  useEffect(() => {
    routeRoomIdRef.current = roomId;
  }, [roomId]);

  useEffect(() => {
    selectedRoomIdRef.current = selectedRoom?.id ?? null;
  }, [selectedRoom?.id]);

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  useEffect(() => {
    loadRooms();
    loadBuildings();
    initializeSocket();

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);

  useEffect(() => {
    let timer: number | undefined;
    const onNotificationsChanged = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        loadRooms(true);
      }, 350);
    };
    window.addEventListener('ebms:notifications-changed', onNotificationsChanged);
    const pollId = window.setInterval(() => {
      loadRoomsRef.current(true);
    }, 15000);
    return () => {
      window.removeEventListener('ebms:notifications-changed', onNotificationsChanged);
      window.clearTimeout(timer);
      window.clearInterval(pollId);
    };
  }, [loadRooms]);

  useEffect(() => {
    if (selectedRoom && selectedBuildingId && selectedRoom.buildingId !== selectedBuildingId) {
      setSelectedRoom(null);
      setMessages([]);
      navigate('/chat');
    }
  }, [selectedBuildingId, selectedRoom, navigate]);

  const searchApartments = useCallback(async (query: string) => {
    if (!selectedBuildingId) return [];

    const result = await apartmentsApi.getApartmentsPage({
      buildingId: selectedBuildingId,
      search: query,
      limit: 20,
    });

    return result.data.map((apartment) => ({
      id: apartment.id,
      label: apartment.code,
      subtitle: apartment.ownerName,
    }));
  }, [selectedBuildingId]);

  useEffect(() => {
    if (roomId) {
      selectRoom(roomId);
    }
  }, [roomId]);

  // Handle reading status separately
  useEffect(() => {
    if (selectedRoom && selectedRoom.id) {
      markRoomAsRead(selectedRoom.id);
    }
  }, [selectedRoom?.id]);

  const loadBuildings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/buildings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
        setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
      }
    } catch (error) {
      console.error('Error loading buildings:', error);
    }
  };

  const resetCreateChatForm = () => {
    setSelectedApartmentId('');
    setSelectedApartmentLabel('');
  };

  const selectRoom = async (selectedRoomId: string) => {
    selectedRoomIdRef.current = selectedRoomId;
    try {
      setLoadingMessages(true);
      console.log('Selecting room:', selectedRoomId);
      
      // Find room in list or fetch it
      let room = rooms.find(r => r.id === selectedRoomId);
      if (!room) {
        console.log('Room not in list, fetching from API...');
        room = await chatApi.getRoom(selectedRoomId);
      }
      
      console.log('Room selected:', room);
      setSelectedRoom(room);
      
      // Load messages
      console.log('Loading messages for room:', selectedRoomId);
      const messageData = await chatApi.getMessages(selectedRoomId);
      console.log('Messages loaded:', messageData);
      setMessages(messageData.reverse()); // Reverse to show oldest first

      // Join socket room (use ref — state may not be ready yet on first open)
      socketRef.current?.emit('joinRoom', { roomId: selectedRoomId });

      // Update URL
      navigate(`/chat/${selectedRoomId}`, { replace: true });
    } catch (error) {
      console.error('Error selecting room:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const initializeSocket = () => {
    const token = localStorage.getItem('token');
    console.log('Token from localStorage:', token ? 'Present' : 'Missing');
    console.log('Token value:', token);
    
    if (!token) {
      console.log('No token found, cannot initialize socket');
      return;
    }

    // Use production API URL for WebSocket connection
    const WS_BASE_URL = getBackendOrigin();
    
    console.log('🔌 [ChatPage] Connecting to WebSocket:', `${WS_BASE_URL}/chat`);
    
    const socketInstance = io(`${WS_BASE_URL}/chat`, {
      auth: {
        token: `Bearer ${token}`
      }
    });

    socketInstance.on('connect', () => {
      console.log('Socket connected successfully');
      setIsConnected(true);
      joinAllChatSocketRooms();
    });

    socketInstance.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    socketInstance.on('newMessage', (message: Message) => {
      console.log('New message received:', message);
      const messageRoomId = message.chatId;
      if (!messageRoomId) {
        return;
      }
      const activeRoomId = selectedRoomIdRef.current ?? routeRoomIdRef.current ?? null;
      const currentUserId = userIdRef.current;
      const isOwn = Boolean(currentUserId && message.senderId === currentUserId);
      const isActiveRoom = Boolean(activeRoomId && messageRoomId === activeRoomId);

      if (!isOwn) {
        const bumpNotificationBadge = () => {
          void refreshCountsRef.current();
        };
        bumpNotificationBadge();
        window.setTimeout(bumpNotificationBadge, 500);
        window.setTimeout(bumpNotificationBadge, 1500);
        window.dispatchEvent(new CustomEvent('ebms:notifications-changed'));
      }

      if (isActiveRoom) {
        void syncOpenChatMessagesRef.current();
        if (!isOwn) {
          chatApi.markAsRead(messageRoomId).catch(() => {});
        }
      }

      setRooms((prev) => {
        const idx = prev.findIndex((r) => r.id === messageRoomId);
        if (idx === -1) {
          void loadRoomsRef.current(true);
          return prev;
        }
        const listMessage: Message = {
          ...message,
          chatId: messageRoomId,
          isRead: isOwn || isActiveRoom,
        };
        const room = {
          ...prev[idx],
          updatedAt: message.createdAt,
          messages: [listMessage],
        };
        const next = [...prev];
        next.splice(idx, 1);
        next.unshift(room);
        roomsRef.current = next;
        return next;
      });
    });

    socketInstance.on('joinedRoom', (data: { roomId: string }) => {
      console.log('Successfully joined room:', data.roomId);
    });

    socketInstance.on('error', (error: { message: string }) => {
      console.error('Socket error:', error.message);
    });

    setSocket(socketInstance);
    socketRef.current = socketInstance;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedRoom || !socket) {
      console.log('Cannot send message:', {
        hasMessage: !!newMessage.trim(),
        hasFile: !!selectedFile,
        hasRoom: !!selectedRoom,
        hasSocket: !!socket
      });
      return;
    }

    // Check if room is locked - block ALL users until unlocked
    if (selectedRoom.isLocked) {
      alert(t('Chat is locked. Please unlock it first to send messages.'));
      return;
    }

    try {
      setUploadingFile(true);

      if (selectedFile) {
        // Send message with file attachment
        console.log('Sending message with file:', {
          roomId: selectedRoom.id,
          content: newMessage.trim(),
          fileName: selectedFile.name,
          fileSize: selectedFile.size
        });

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('content', newMessage.trim());

        const message = await chatApi.sendMessageWithFile(selectedRoom.id, formData);
        console.log('Message with file sent via API:', message);
        
        // Add message immediately for instant display
        // The socket event will arrive later, but we'll prevent duplicates by checking message ID
        setMessages(prev => {
          // Check if message already exists to prevent duplicates
          const messageExists = prev.some(m => m.id === message.id);
          if (messageExists) {
            return prev; // Don't add if already exists
          }
          return [...prev, message];
        });
        setRooms((prev) => {
          const idx = prev.findIndex((r) => r.id === message.chatId);
          if (idx === -1) {
            return prev;
          }
          const room = {
            ...prev[idx],
            updatedAt: message.createdAt,
            messages: [{ ...message, isRead: true }],
          };
          const next = [...prev];
          next.splice(idx, 1);
          next.unshift(room);
          return next;
        });
      } else {
        // Send text message via socket
        console.log('Sending text message:', {
          roomId: selectedRoom.id,
          content: newMessage.trim(),
          socketConnected: socket.connected
        });
        
        socket.emit('sendMessage', {
          roomId: selectedRoom.id,
          content: newMessage.trim()
        });
      }

      setNewMessage('');
      clearSelectedFile();
      console.log('Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleAddChat = async () => {
    if (!selectedBuildingId || !selectedApartmentId) return;

    try {
      const room = await chatApi.getOrCreateRoomForApartment(selectedApartmentId);
      await loadRooms();
      setShowAddChatModal(false);
      resetCreateChatForm();
      navigate(`/chat/${room.id}`);
    } catch (error) {
      console.error('Error creating chat:', error);
      alert('Có lỗi xảy ra khi tạo cuộc trò chuyện');
    }
  };

  const handleLockToggle = async () => {
    if (!selectedRoom) return;

    try {
      let updatedRoom: ChatRoom;
      if (selectedRoom.isLocked) {
        updatedRoom = await chatApi.unlockRoom(selectedRoom.id);
      } else {
        updatedRoom = await chatApi.lockRoom(selectedRoom.id);
      }
      
      setSelectedRoom(updatedRoom);
      setRooms(prev => prev.map(room => 
        room.id === updatedRoom.id ? updatedRoom : room
      ));
    } catch (error) {
      console.error('Error toggling room lock:', error);
      alert('Có lỗi xảy ra khi thay đổi trạng thái khóa');
    }
  };

  // Image modal handlers
  const openImageModal = (imageUrl: string, fileName: string) => {
    setImageModal({
      isOpen: true,
      imageUrl,
      fileName
    });
  };

  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: '',
      fileName: ''
    });
  };

  const openImageInNewTab = () => {
    window.open(imageModal.imageUrl, '_blank');
  };

  const markRoomAsRead = async (roomId: string) => {
    try {
      await chatApi.markAsRead(roomId);
      setRooms(prev => prev.map(room => {
        if (room.id === roomId) {
          return {
            ...room,
            messages: room.messages.map(msg => ({ ...msg, isRead: true }))
          };
        }
        return room;
      }));
      window.dispatchEvent(new CustomEvent('ebms:notifications-changed'));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const hasUnreadMessages = (room: ChatRoom) => {
    return room.messages.some(
      (message) => !message.isRead && message.senderId !== user?.id,
    );
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('vi-VN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getChatTitle = (room: ChatRoom) => {
    const apartmentParticipant = room.participants.find(p => p.apartment);
    if (apartmentParticipant?.apartment) {
      return formatApartmentChatTitle(apartmentParticipant.apartment);
    }
    return `Chat ${room.id.substring(0, 8)}`;
  };

  const getLastMessage = (room: ChatRoom) => {
    if (room.messages && room.messages.length > 0) {
      const lastMessage = room.messages[0];
      return {
        content: lastMessage.content,
        time: formatMessageTime(lastMessage.createdAt),
        sender: lastMessage.sender.name
      };
    }
    return null;
  };

  const renderMessage = (message: Message) => {
    return (
      <SecureMessage
        key={message.id}
        message={message}
        user={user}
        room={selectedRoom}
        onImageClick={openImageModal}
      />
    );
  };

  const filteredRooms = rooms.filter((room) => {
    if (selectedBuildingId && room.buildingId !== selectedBuildingId) {
      return false;
    }
    const title = getChatTitle(room).toLowerCase();
    return title.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="flex h-[calc(100vh-8.5rem)] min-h-0 -mx-6 -mb-6 bg-gray-50">
      {/* Left Side - Chat List */}
      <div className="w-1/3 bg-white border-r border-gray-200 flex flex-col min-h-0">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-gray-900 flex items-center">
              <ChatBubbleLeftIcon className="h-6 w-6 mr-2 text-blue-600" />
              Tin nhắn
            </h1>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} 
                   title={isConnected ? 'Đã kết nối' : 'Mất kết nối'} />
              <button
                onClick={() => {
                  if (!selectedBuildingId) {
                    toast.error(t('selectBuildingFirst'));
                    return;
                  }
                  resetCreateChatForm();
                  setShowAddChatModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
                title="Tạo cuộc trò chuyện mới"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {shouldShowBuildingSelector(buildings) && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('selectBuilding')} *
            </label>
            <select
              value={selectedBuildingId}
              onChange={(e) => {
                selectBuilding(user?.id, e.target.value, setSelectedBuildingId);
                resetCreateChatForm();
              }}
                disabled={!needsBuildingSelection(buildings)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('selectBuildingPlaceholder')}</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
          )}
          
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Tìm kiếm cuộc trò chuyện..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {!selectedBuildingId ? (
            <div className="text-center p-8 text-gray-500">
              <BuildingOfficeIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>{t('selectBuildingFirst')}</p>
            </div>
          ) : loadingRooms ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredRooms.length === 0 ? (
            <div className="text-center p-8 text-gray-500">
              <ChatBubbleLeftIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>{searchTerm ? 'Không tìm thấy cuộc trò chuyện nào' : 'Chưa có cuộc trò chuyện nào'}</p>
              {!searchTerm && (
                <button
                  onClick={() => {
                    resetCreateChatForm();
                    setShowAddChatModal(true);
                  }}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Tạo cuộc trò chuyện mới
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredRooms.map((room) => {
                const lastMessage = getLastMessage(room);
                const isSelected = selectedRoom?.id === room.id;
                const hasUnread = hasUnreadMessages(room);
                
                return (
                  <div
                    key={room.id}
                    onClick={() => {
                      selectRoom(room.id);
                    }}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-blue-50 border-r-2 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 relative">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        {hasUnread && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                            <ExclamationCircleIcon className="h-3 w-3 text-white" />
                          </div>
                        )}
                        {room.isLocked && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                            <LockClosedIcon className="h-2.5 w-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className={`text-sm truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-900'}`}>
                            {getChatTitle(room)}
                          </h3>
                          <div className="flex items-center space-x-2">
                            {lastMessage && (
                              <span className={`text-xs ${hasUnread ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                                {lastMessage.time}
                              </span>
                            )}
                            {hasUnread && (
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            )}
                          </div>
                        </div>
                        {lastMessage ? (
                          <p className={`text-sm truncate mt-1 ${hasUnread ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                            <span className="font-medium">{lastMessage.sender}:</span> {lastMessage.content}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400 mt-1">
                            Chưa có tin nhắn
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Chat Room */}
      <div className="flex-1 flex flex-col min-h-0">
        {selectedRoom ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center relative">
                    <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                    {selectedRoom.isLocked && (
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                        <LockClosedIcon className="h-2.5 w-2.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                      <span>{getChatTitle(selectedRoom)}</span>
                      {selectedRoom.isLocked && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <LockClosedIcon className="h-3 w-3 mr-1" />
                          Đã khóa
                        </span>
                      )}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {selectedRoom.participants.length} người tham gia
                      {selectedRoom.isLocked && selectedRoom.lockedAt && (
                        <span className="ml-2">• Khóa lúc {formatMessageTime(selectedRoom.lockedAt)}</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Lock/Unlock button (staff only) */}
                  {(user?.role === 'SUPERADMIN' || user?.role === 'OPERATOR' || user?.role === 'STAFF') && (
                    <button
                      onClick={handleLockToggle}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedRoom.isLocked
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                      title={selectedRoom.isLocked ? 'Mở khóa cuộc trò chuyện' : 'Khóa cuộc trò chuyện'}
                    >
                      {selectedRoom.isLocked ? (
                        <>
                          <LockOpenIcon className="h-4 w-4" />
                          <span>Mở khóa</span>
                        </>
                      ) : (
                        <>
                          <LockClosedIcon className="h-4 w-4" />
                          <span>Khóa</span>
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedRoom(null);
                      setMessages([]);
                      navigate('/chat');
                    }}
                    className="md:hidden p-2 text-gray-400 hover:text-gray-600"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* Locked status message */}
              {selectedRoom.isLocked && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <LockClosedIcon className="h-5 w-5 text-red-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        {t('Chat is locked')}
                      </p>
                      <p className="text-xs text-red-600">
                        {t('No one can send messages until the chat is unlocked. Only staff can unlock it.')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <ChatBubbleLeftIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Chưa có tin nhắn nào</p>
                  <p className="text-sm mt-2">Hãy bắt đầu cuộc trò chuyện!</p>
                </div>
              ) : (
                messages.map(renderMessage)
              )}
            </div>

            {/* Message Input */}
            <div className="flex-shrink-0 bg-white border-t border-gray-200 p-4">
              {/* Locked room message for all users */}
              {selectedRoom.isLocked && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <LockClosedIcon className="h-5 w-5 text-red-600 mr-2" />
                    <p className="text-sm text-red-800">
                      {t('Chat is locked. Please unlock it first to send messages.')}
                    </p>
                  </div>
                </div>
              )}

              {/* File Preview */}
              {selectedFile && (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Đính kèm file: {selectedFile.name}
                    </span>
                    <button
                      onClick={clearSelectedFile}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {filePreview && (
                    <img
                      src={filePreview}
                      alt="Preview"
                      className="max-w-32 h-auto rounded-lg"
                    />
                  )}
                  
                  <div className="text-xs text-gray-500 mt-1">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
              )}

              {/* Input field - disabled when room is locked */}
              {!selectedRoom.isLocked && (
                <div className="flex items-center space-x-2">
                  {/* File attachment button */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-gray-400 hover:text-gray-600 p-2 rounded-lg transition-colors"
                    disabled={uploadingFile}
                  >
                    <PhotoIcon className="h-5 w-5" />
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={selectedFile ? t("Add description (optional)...") : t("Type a message...")}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={!isConnected || uploadingFile}
                  />
                  
                  <button
                    onClick={sendMessage}
                    disabled={(!newMessage.trim() && !selectedFile) || !isConnected || uploadingFile}
                    className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {uploadingFile ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <PaperAirplaneIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              )}
              
              {!isConnected && (
                <p className="text-sm text-red-500 mt-2">
                  Mất kết nối - đang thử kết nối lại...
                </p>
              )}
            </div>
          </>
        ) : (
          /* No Room Selected */
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <ChatBubbleLeftIcon className="h-24 w-24 mx-auto mb-6 text-gray-300" />
              <h2 className="text-2xl font-semibold text-gray-600 mb-2">
                Chọn cuộc trò chuyện
              </h2>
              <p className="text-gray-500 mb-4">
                Chọn một cuộc trò chuyện từ danh sách bên trái để bắt đầu
              </p>
              <button
                onClick={() => {
                  if (!selectedBuildingId) {
                    toast.error(t('selectBuildingFirst'));
                    return;
                  }
                  resetCreateChatForm();
                  setShowAddChatModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Tạo cuộc trò chuyện mới
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Chat Modal */}
      {showAddChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Tạo cuộc trò chuyện mới
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('selectBuilding')}
              </label>
              <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700">
                {buildings.find((building) => building.id === selectedBuildingId)?.name || t('selectBuildingFirst')}
              </div>
            </div>

            <div className="mb-4">
              <AsyncSearchPicker
                label={t('selectApartment')}
                placeholder={t('searchApartmentPlaceholder')}
                value={selectedApartmentId}
                selectedLabel={selectedApartmentLabel}
                onChange={(id, option) => {
                  setSelectedApartmentId(id);
                  setSelectedApartmentLabel(option?.label || '');
                }}
                onSearch={searchApartments}
                disabled={!selectedBuildingId}
                minCharsHint={t('searchMinCharsHint')}
                searchingLabel={t('searching')}
                noResultsLabel={t('noSearchResults')}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddChatModal(false);
                  resetCreateChatForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={handleAddChat}
                disabled={!selectedBuildingId || !selectedApartmentId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Tạo cuộc trò chuyện
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={closeImageModal}
        fileName={imageModal.fileName}
        fileUrl={imageModal.imageUrl}
      />
    </div>
  );
};

export default ChatPage;